import type { CSSProperties, ReactNode } from "react";
import { RiskBadge } from "./RiskBadge";
import type { Risk } from "./risk";
import { useAdminT } from "./useAdminT";
import { useShowKeys } from "./useShowKeys";

export interface SettingRowEntry {
  name: string;
  description: string;
  risk: Risk;
}

interface Props {
  settingKey: string;
  /** Catalog entry; absent for a DB row the catalog does not know. */
  entry?: SettingRowEntry;
  children?: ReactNode;
}

const WRAP: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 8, padding: 16,
  border: "1px solid var(--d-line)", borderRadius: 20, scrollMarginTop: 80,
};
const HEAD: CSSProperties = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const NAME: CSSProperties = {
  fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 15, color: "var(--d-fg)",
};
const DESC: CSSProperties = {
  fontFamily: "var(--font-body)", fontSize: 13, color: "var(--d-fg-muted)",
};
const KEY: CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--d-fg-muted)",
};

export function SettingRow({ settingKey, entry, children }: Props): JSX.Element {
  const t = useAdminT();
  const { showKeys } = useShowKeys();
  return (
    <section id={`setting-${settingKey}`} data-setting-key={settingKey} style={WRAP}>
      <div style={HEAD}>
        <span style={NAME}>{entry?.name ?? settingKey}</span>
        {entry ? <RiskBadge risk={entry.risk} /> : null}
      </div>
      <div style={DESC}>{entry?.description ?? t("shell.no_description")}</div>
      {showKeys ? <code data-testid="setting-key" style={KEY}>{settingKey}</code> : null}
      {children}
    </section>
  );
}
